import { useContext, useEffect, useRef } from 'react';
import { useForm, useWatch, FormProvider } from 'react-hook-form';
import { getPendingChanges, ManagerOverlappingFilter, BidRange, CampaignType } from 'entities/campaign';
import type { AuthenticatedContractorModel } from 'entities/profile';
import { useGetUser, useIsBlockingTime, useIsElementBlocked } from 'entities/profile';
import { FormErrors } from 'shared/ui/FormErrors';
import { Input } from 'shared/ui/Input';
import { FormSelect } from 'shared/ui/Select';
import { SideModalBody, AlertModal, SlideModalContext } from 'shared/ui/SideModal';
import { getValueFromString } from 'shared/lib/formatting';
import { getDataAutoTestAttributes } from 'shared/lib/data-auto-test';
import { useHidable } from 'shared/lib/hooks';
import { form } from 'shared/lib/form';
import { showErrorToast } from 'shared/lib/notifications';
import { buildQueryString } from 'shared/lib/router';
import { useGetCampaignHistory, type HistoryTableColumns } from 'entities/history';
import { pageRoutes } from 'shared/routes';
import { EditEmails, EditIntegrations, useShowCallCenterBanner } from 'features/edit-integrations';
import { EditZips, useZipsActions, useZipsNumber } from 'features/edit-zips';
import { ContractorCampaignDetails, DeactivateCampaign, ManagerCampaignDetails } from 'features/change-campaign-status';
import { MinIncomeField } from 'features/edit-min-income';
import { CallCenterBanner, CallCenterTime } from 'features/call-center-hours';
import { OverlappingField } from 'features/campaign-overlapping-info';
import { GroupsField } from 'features/show-campaign-groups';
import { LeadsReceivingTime } from 'features/edit-scheduling';
import { ShowHistory } from 'features/show-history';
import { EstimationTool } from 'features/estimator';
import { ShowNotes } from 'features/show-campaign-notes';
import { isBoolean } from 'shared/lib/type-guards';
import { getDbaOptions } from 'entities/contractor';

import { WeeklyBudgetField } from './WeeklyBudgetField';
import {
  getCampaignFormValues,
  isManagerCampaignModel,
  isShowSimplybookUrl,
  useModalPages,
  getIsBudgetIncrease,
  historyTableColumns,
  useSaveCampaign,
} from './lib';
import { HighlightedChangesInfo } from './HighlightedChangesInfo';
import { CampaignEditModalFooter } from './CampaignEditModalFooter';
import type { CampaignEditModalBodyProps } from './model';
import { CAMPAIGN_EDITING_DISABLED_MESSAGE, companyTypesOptions } from './constants';
import { AcceptOtherServices } from './AcceptOtherServices';

export function CampaignEditModalBody({
  campaign,
  afterSuccessSaveHandler,
  afterCampaignOnHoldHandler,
  afterCampaignDeactivation,
  afterCampaignReactivate,
  skipCampaignRefetch,
  skipCampaignUpdate,
}: CampaignEditModalBodyProps) {
  const isBlockingTime = useIsBlockingTime();
  const isBlockingTimeRef = useRef(isBlockingTime);
  const isToggleBlocked = useIsElementBlocked('toggle');
  const isZipsBlocked = useIsElementBlocked('zips');
  const isBudgetBlocked = useIsElementBlocked('budget');
  const isScheduleBlocked = useIsElementBlocked('schedule');
  const { isShowCallCenterBanner, isShowCallCenterTime } = useShowCallCenterBanner({ campaign });
  const isManagerCampaign = isManagerCampaignModel(campaign);
  const useGetHistory = () => useGetCampaignHistory(campaign.id);
  const editDialog = useHidable(true);
  const methods = useForm(getCampaignFormValues(campaign));
  const { reset } = useZipsActions();
  const zipsNumber = useZipsNumber();
  const isDirtyForm = form(methods) || !!zipsNumber;
  const isApptCampaign = campaign.type === CampaignType.appointments;
  const pausedReasonFromData = campaign.pausedReasonId?.toString() || '';
  const showIntegrations = isManagerCampaign && !isApptCampaign;
  const disabledBidRange = !editDialog.isShown || (campaign.type === CampaignType.sales && !isManagerCampaign);
  const showBidRange = isManagerCampaign || campaign.type !== CampaignType.sales;
  const setRate = isApptCampaign ? campaign.service.setRate : undefined;
  const { saveCampaign, isCampaignUpdating, updateCampaignError } = useSaveCampaign({
    campaign,
    isManagerCampaign,
    skipCampaignRefetch,
    afterSuccessSaveHandler,
  });

  const userData = useGetUser<AuthenticatedContractorModel>().data?.data;

  const getDbas = () => {
    if (isManagerCampaign) {
      return campaign.contractor.dbas;
    }

    return userData?.dbas ?? [];
  };

  const getCompanyName = () => {
    if (isManagerCampaign) {
      return campaign.contractor.companyName;
    }

    return userData?.companyName ?? '';
  };

  useEffect(() => {
    const prevBlockingStatus = isBlockingTimeRef.current;

    // We need this logic happens only once when blocking time has come
    if (isBlockingTime && prevBlockingStatus !== isBlockingTime) {
      showErrorToast(CAMPAIGN_EDITING_DISABLED_MESSAGE);
      methods.reset();
      editDialog.hide();
    }
  }, [isBlockingTime]);

  useEffect(() => reset, []);

  const currentBudget = getValueFromString(
    useWatch({
      control: methods.control,
      name: 'budget',
    }) ?? ''
  );
  const minIncome = getValueFromString(
    useWatch({
      control: methods.control,
      name: 'minimalIncome',
    }) ?? ''
  );
  const newPausedReason =
    useWatch({
      control: methods.control,
      name: 'pausedReason',
    }) ?? '';
  const isBudgetIncrease = getIsBudgetIncrease(campaign.budget?.amount, currentBudget);
  const pendingChanges = getPendingChanges(campaign, {
    minimalIncome: !!methods.formState.dirtyFields.minimalIncome,
    budget: !!methods.formState.dirtyFields.budget && !isBudgetIncrease,
    zipCodes: !!zipsNumber,
  });
  const showDeactivateBox = isManagerCampaign && campaign.status !== 'deactivated';
  const submit = methods.handleSubmit(saveCampaign);

  const onOverlappingViewHandler = () => {
    if (isManagerCampaign) {
      window.open(
        `${pageRoutes.internal.overlapping}?${buildQueryString({
          [ManagerOverlappingFilter.campaignId]: campaign.id,
          [ManagerOverlappingFilter.contractorIdArray]: campaign.contractor.id,
        })}`
      );
    }
  };

  const appliesAt = campaign.pendingChanges?.appliesAt ?? pendingChanges.appliesAt;
  const { transitionEnd } = useContext(SlideModalContext);
  const { isMinIncome, isWeeklyBudget, isBidRange } = useModalPages(editDialog.show, campaign.id);

  const dbaOptions = getDbaOptions(getCompanyName(), getDbas());
  const selectedOptionId = typeof campaign.dbaId === 'number' ? campaign.dbaId.toString() : '0';

  useEffect(() => {
    if (pausedReasonFromData !== newPausedReason) {
      editDialog.show();
    }
  }, [newPausedReason]);

  return (
    <FormProvider {...methods}>
      <SideModalBody title="Campaign details">
        {isManagerCampaign ? (
          <ManagerCampaignDetails
            campaign={campaign}
            afterSwitchOff={afterCampaignOnHoldHandler}
            afterCampaignReactivate={afterCampaignReactivate}
            skipCampaignRefetch={skipCampaignRefetch}
            skipCampaignUpdate={skipCampaignUpdate}
          />
        ) : (
          <ContractorCampaignDetails
            campaign={campaign}
            isBlockingTime={isToggleBlocked}
          />
        )}
        <form
          className="pb-8"
          onSubmit={submit}
        >
          <AlertModal
            autoTestAttributesName="campaign-details-exit-modal"
            showCondition={isDirtyForm}
          />
          <FormErrors error={updateCampaignError} />
          <div className="mb-4 mt-8 flex items-center justify-between">
            <div className="text-lg font-semibold">Edit campaign</div>
          </div>
          <Input
            isDisabled={!editDialog.isShown}
            className="mb-2"
            name="name"
            title="Name"
            type="text"
            {...getDataAutoTestAttributes(['input-campaign-edit-name'])}
          />
          {isManagerCampaign && (
            <Input
              isDisabled
              className="mb-2"
              name="contractor"
              title="Contractor"
              type="text"
              {...getDataAutoTestAttributes(['input-campaign-edit-contractor'])}
            />
          )}
          {dbaOptions.length > 1 && (
            <FormSelect
              wrapperClass="mb-2"
              isRequired
              isDisabled={!editDialog.isShown}
              title="DBA/company name"
              name="dbaId"
              options={dbaOptions}
              selectedOptionId={selectedOptionId}
              titleTooltipText="The DBA/company name will be displayed to the homeowner, informing them who will be in contact about the project."
              titleTooltipTextPosition="top"
              {...getDataAutoTestAttributes(['select-campaign-edit-type'])}
            />
          )}
          <FormSelect
            wrapperClass="mb-2"
            isRequired
            isDisabled
            title="Type"
            name="type"
            options={companyTypesOptions}
            selectedOptionId={campaign.type}
            {...getDataAutoTestAttributes(['select-campaign-edit-type'])}
          />
          <EditZips
            isBlockingTime={isZipsBlocked}
            isEditMode={editDialog.isShown}
            pendingChanges={pendingChanges.zipCodes}
            campaign={campaign}
          />
          {isManagerCampaign && editDialog.isShown && <EstimationTool estimations={campaign.estimations} />}
          {isManagerCampaign && (
            <MinIncomeField
              campaignId={campaign.id}
              isEdit={editDialog.isShown}
              pendingChanges={pendingChanges.minimalIncome}
              minIncome={minIncome}
              isFocused={isMinIncome && transitionEnd}
              appliesAt={appliesAt}
            />
          )}
          <FormSelect
            isDisabled
            name="serviceId"
            isRequired
            title="Service"
            className="mb-2"
            wrapperClass="w-full"
            selectedOptionId="0"
            options={[{ text: campaign.service.name || '', id: '0' }]}
            {...getDataAutoTestAttributes(['select-campaign-edit-service'])}
          />
          {isManagerCampaign && isBoolean(campaign.acceptOtherServices) && (
            <AcceptOtherServices
              isDisabled={!editDialog.isShown}
              isChecked={campaign.acceptOtherServices}
            />
          )}
          {showBidRange && (
            <BidRange
              dataTestId="bidRange"
              className="mb-2"
              isDisabled={disabledBidRange}
              isFocused={isBidRange && transitionEnd}
              campaignType={campaign.type}
              bidParams={campaign.bid ? campaign.service : null}
              bidTierGroups={campaign.bidTierGroups}
              isLessThanMinimumTooltip
              setRate={setRate}
            />
          )}
          {campaign.type !== 'sales' && (
            <WeeklyBudgetField
              editDialogIsShown={editDialog.isShown}
              isBudgetBlocked={isBudgetBlocked}
              isWeeklyBudget={isWeeklyBudget}
              transitionEnd={transitionEnd}
              pendingChanges={pendingChanges}
              appliesAt={appliesAt}
            />
          )}
          {isShowSimplybookUrl(campaign) && (
            <Input
              className="mt-2"
              isDisabled={!editDialog.isShown}
              name="simplybookUrl"
              title="Calendar URL"
              type="text"
              {...getDataAutoTestAttributes(['input-campaign-edit-calendar-url'])}
            />
          )}
          {isShowCallCenterBanner && (
            <CallCenterBanner
              campaign={campaign}
              timeZone={campaign.timezone}
              isShowClock={pendingChanges.callCenterSchedule}
              appliesAt={campaign.pendingChanges?.appliesAt ?? null}
            />
          )}
          {isShowCallCenterTime && (
            <CallCenterTime
              campaign={campaign}
              isBlockingTime={isScheduleBlocked}
              isShowClock={pendingChanges.callCenterSchedule}
              appliesAt={campaign.pendingChanges?.appliesAt ?? null}
            />
          )}
          {isManagerCampaign && (
            <OverlappingField
              campaignId={campaign.id}
              campaignType={campaign.type}
              onViewClick={onOverlappingViewHandler}
              appliesAt={appliesAt}
            />
          )}
          {isManagerCampaign && <GroupsField id={campaign.id} />}
          {!isShowCallCenterBanner && !isShowCallCenterTime && campaign.type !== 'appt' && (
            <LeadsReceivingTime
              campaign={campaign}
              isShowClock={pendingChanges.schedule}
              isBlockingTime={isScheduleBlocked}
              appliesAt={appliesAt}
            />
          )}
          {showIntegrations && (
            <>
              <EditIntegrations campaign={campaign} />
              <EditEmails
                campaignId={campaign.id}
                emails={campaign.campaignEmailIntegrations?.emails}
              />
            </>
          )}
          <HighlightedChangesInfo
            pendingChanges={pendingChanges}
            status={campaign.status}
            appliesAt={appliesAt}
          />
          {isManagerCampaign && (
            <div className="mt-8">
              <ShowNotes campaignId={campaign.id} />
            </div>
          )}
          {isManagerCampaign && (
            <div className="mt-8">
              <ShowHistory
                text="History"
                iconName="history"
                useGetHistory={useGetHistory}
                historyTableColumns={historyTableColumns(campaign.id) as HistoryTableColumns}
                attributes={getDataAutoTestAttributes(['button-campaign-show-history'])}
              />
            </div>
          )}
        </form>
        {showDeactivateBox && (
          <DeactivateCampaign
            campaign={campaign}
            onSuccessHandler={afterCampaignDeactivation}
          />
        )}
      </SideModalBody>
      <CampaignEditModalFooter
        isEditMode={editDialog.isShown}
        isLoading={isCampaignUpdating}
        submit={submit}
      />
    </FormProvider>
  );
}
